import { useEffect, useState, useRef } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Card, CardContent } from "@/components/ui/card"
import { useSession } from "../contexts/SessionContext"
import { apiFetch } from "@prescreen/lib/api-client"

export default function LoginMagicLinkPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refetch } = useSession()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const verifyStartedRef = useRef(false)

  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const callbackUrl = searchParams.get('callbackUrl')

  useEffect(() => {
    if (!token || !email) {
      setStatus('error')
      setErrorMessage('Invalid sign-in link. Please request a new one.')
      return
    }

    if (verifyStartedRef.current) return
    verifyStartedRef.current = true

    const verify = async () => {
      try {
        const res = await apiFetch('/api/auth/verify', {
          method: 'POST',
          body: JSON.stringify({
            token,
            email,
            callbackUrl: callbackUrl || undefined,
          }),
        })

        const data = await res.json()

        if (!res.ok) {
          setStatus('error')
          if (data.error === 'Verification' || data.error === 'InvalidVerificationLink') {
            setErrorMessage(data.message || 'This sign-in link is invalid or has expired. Please request a new one.')
          } else {
            setErrorMessage(data.message || 'Sign-in failed. Please try again.')
          }
          return
        }

        await refetch()
        const redirectTo = (data.callbackUrl && typeof data.callbackUrl === 'string') ? data.callbackUrl : '/'
        navigate(redirectTo, { replace: true })
      } catch {
        setStatus('error')
        setErrorMessage('Something went wrong. Please try again.')
      }
    }

    verify()
  }, [token, email, callbackUrl, refetch, navigate])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md p-8">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
              aria-hidden
            />
            <p className="mt-4 text-gray-600">Signing you in...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Sign-in failed</h1>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Back to sign in
            </button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md p-8">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <div
            className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
            aria-hidden
          />
          <p className="mt-4 text-gray-600">Signing you in...</p>
        </CardContent>
      </Card>
    </div>
  )
}
